import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import mediterraneanBackground from '../../images/mediterranean.png';
import summerMedDietImage from '../../images/summer-med-diet.webp';
import mediterranianFoodsListImage from '../../images/mediterranian-foods-list.jpg';
import medWesternImage from '../../images/med-western.webp';
import medDiet31DayImage from '../../images/31-Day-Mediterranean-Diet.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/mediterranean';
const PAGE_TITLE = 'Mediterranean Diet Guide: Heart Health and Longevity - Create A Dish';
const PAGE_DESCRIPTION = 'Emphasizes plant-based foods, healthy fats (olive oil), whole grains, and lean proteins (fish). It is widely considered the "gold standard" for heart health and longevity.';
const PAGE_IMAGE = 'https://createadish.com/images/mediterranean-og.png';

const Mediterranean: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="mediterranean diet, heart health, longevity, balanced diet, olive oil, healthy fats, whole grains, lean proteins" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={PAGE_URL} />
        
        {/* Structured Data - Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Mediterranean Diet Guide: Heart Health and Longevity",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            },
            "keywords": "HeartHealth, Longevity, Balanced"
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${mediterraneanBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              Mediterranean Diet
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Heart Health and Longevity
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Mediterranean Diet
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            The Mediterranean Diet
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Emphasizes plant-based foods, healthy fats (olive oil), whole grains, and lean proteins (fish). It is widely considered the "gold standard" for heart health and longevity.
          </Typography>

          {/* Tags */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #HeartHealth
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #Longevity
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #Balanced
            </Typography>
          </Box>

          {/* Intro Section */}
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Mediterranean Diet, a timeless eating pattern inspired by the traditional foods of countries bordering the Mediterranean Sea. This diet emphasizes plant-based foods, healthy fats like olive oil, whole grains, and lean proteins such as fish, while limiting processed items and red meat. Widely regarded as the "gold standard" for heart health and longevity, it promotes not just nutrition but a lifestyle of mindful eating and enjoyment. Our AI recipe generator can help you create customized dishes that align with Mediterranean principles—simply input your preferences for fresh, wholesome meals.
          </Typography>
          <Box component="img" src={summerMedDietImage} alt="Mediterranean diet foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          {/* Historical Background */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Mediterranean Diet has roots stretching back thousands of years, influenced by ancient agricultural practices in regions like Greece, Italy, and Spain. Historical records show that early diets in these areas featured staples such as wheat, barley, grapes, olives, and legumes, shaped by the region's climate and trade routes. It wasn't formally "invented" but evolved naturally from Middle Ages food patterns in Greece and surrounding areas, incorporating simple, seasonal ingredients. In the mid-20th century, researchers like Ancel Keys popularized it through the Seven Countries Study, highlighting its role in reducing heart disease rates compared to Western diets. Today, it's recognized by UNESCO as an Intangible Cultural Heritage of Humanity, blending tradition with modern science for sustainable health.
            </Typography>
          </Box>

          {/* Key Components */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Mediterranean Diet is flexible but follows a pyramid structure: abundant plant foods at the base, moderate dairy and proteins in the middle, and minimal sweets and red meat at the top. Here's a breakdown:
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits and Vegetables</strong>: Aim for 7-10 servings daily. Favorites include tomatoes, leafy greens (spinach, kale), cucumbers, bell peppers, oranges, apples, and berries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Whole Grains</strong>: Brown rice, quinoa, barley, whole-wheat bread, and oats for sustained energy.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Extra-virgin olive oil as the primary fat source; also avocados, nuts (almonds, walnuts), and seeds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Lean Proteins</strong>: Fish (salmon, sardines, tuna) at least twice a week for omega-3s; poultry, eggs, and legumes in moderation.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Dairy</strong>: Low-fat options like Greek yogurt, feta, and cheese, consumed moderately.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Herbs and Spices</strong>: Basil, oregano, garlic, rosemary, and thyme for flavor.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Beverages</strong>: Water as the mainstay; moderate red wine (1-2 glasses daily for adults).</Typography>
            </Box>
            <Box component="img" src={mediterranianFoodsListImage} alt="Mediterranean foods list" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Foods to Limit or Avoid
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Processed foods, sugary drinks, and refined grains</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Excessive red meat, butter, and saturated fats</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Added sugars and sweets—enjoy sparingly</Typography>
            </Box>
          </Box>

          {/* Health Benefits */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Backed by extensive research, the Mediterranean Diet offers numerous evidence-based benefits:
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Heart Health</strong>: Reduces risk of cardiovascular disease by lowering bad cholesterol and blood pressure.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Longevity</strong>: Linked to longer life expectancy and lower rates of type 2 diabetes and certain cancers.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Weight Management</strong>: Promotes satiety with fiber-rich foods.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Mental Health</strong>: Associated with reduced depression and improved cognitive function.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Budget-Friendly</strong>: Cost-effective while boosting health.</Typography>
            </Box>
            <Box component="img" src={medWesternImage} alt="Mediterranean vs Western diet comparison" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          {/* Sample Meal Plan */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Sample Weekly Meal Plan
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              To get started, here's a simple 7-day plan using Mediterranean staples. Use our AI generator to adapt these for your tastes or dietary needs.
            </Typography>
            <Box component="img" src={medDiet31DayImage} alt="31-day Mediterranean diet plan" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          {/* Tips */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Mediterranean Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Small</strong>: Swap butter for olive oil and add more veggies to every meal.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Meal Prep</strong>: Prepare batches of grains and salads for easy assembly.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Incorporate with AI</strong>: Use CreateADish.com to generate recipes like "Mediterranean grilled fish with herbs."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Lifestyle Integration</strong>: Eat mindfully, share meals with others, and stay active.</Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Whether you're aiming for better health or flavorful experiments, the Mediterranean Diet is a versatile foundation. Explore our generator for endless inspirations!
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Mediterranean;
